import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import { Button } from '@codes/ui-kit/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@codes/ui-kit/components/ui/dropdown-menu';
import { Image } from '@codes/ui-kit/components/ui/image';
import { toast } from '@codes/ui-kit/components/ui/use-toast';
import { useQueryClient } from '@tanstack/react-query';
import { useSelectedStore } from '@web/app/shared';
import { ChevronDown, Menu, Moon, Sun, User } from 'lucide-react';

import {
    EnDarkIcon,
    EnHoverIcon,
    EnIcon,
    KrDarkIcon,
    KrHoverIcon,
    KrIcon,
    LogoImage,
    LogoLightImage,
} from '@codes/assets';
import { useOwnerStore } from '@codes/owner';
import { useStorageTransfer } from '@codes/storage-transfer';
import { useTheme } from '@codes/theme';
import { Avatar, AvatarFallback, AvatarImage, Badge, SharedSpinner, cn } from '@codes/ui-kit';
import { useLogout, useWebCoreStore } from '@codes/web-core';

import { ContactModal } from './ContactModal';

interface HeaderProps {
    isSidebarOpen: boolean;
    setIsSidebarOpen: (isOpen: boolean) => void;
}

export const Header = ({ isSidebarOpen, setIsSidebarOpen }: HeaderProps) => {
    const isAuthenticated = useWebCoreStore(state => state.isAuthenticated);
    const ownerInfo = useOwnerStore(state => state.owner);

    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const { theme, setTheme } = useTheme();

    const [language, setLanguage] = useState<string>(i18n.language || 'en');
    const { userName, profile } = useWebCoreStore();
    const { workspace, clearSelection, project } = useSelectedStore();

    const target = import.meta.env.VITE_BOX_HOST.toLowerCase();
    const { transferStatus, handleTransfer } = useStorageTransfer(target);
    const { isTransferring, status } = transferStatus;

    const [showContactModal, setShowContactModal] = useState(false);

    const handleThemeChange = () => {
        setTheme(theme === 'light' ? 'dark' : 'light');
    };

    const toggleLanguage = () => {
        const newLanguage = language === 'en' ? 'ko' : 'en';
        setLanguage(newLanguage);
        i18n.changeLanguage(newLanguage);
    };

    const [headerOptionOpened, setHeaderOptionOpened] = useState(false);

    const logoutMutation = useLogout(async () => {
        toast({
            title: t('auth.successLogout'),
            duration: 2000,
        });
        clearSelection();
        await queryClient.resetQueries();
        setTimeout(() => {
            window.location.reload();
        }, 1000);
    });

    const onTransferClick = async () => {
        const result = await handleTransfer();
        if (result.success) {
            console.log('전송 성공!');
        } else {
            console.error('전송 실패:', result.error);
        }
    };

    const getWorkspaceBadge = (stereo?: string) => {
        switch (stereo) {
            case 'free':
            case 'basic':
                return (
                    <Badge variant="C" className="bg-sub">
                        free
                    </Badge>
                );
            case 'pro':
                return (
                    <Badge variant="C" className="bg-pro">
                        pro
                    </Badge>
                );
            case 'ent':
                return (
                    <Badge variant="C" className="bg-adv">
                        adv
                    </Badge>
                );
            default:
                return null;
        }
    };

    if (!isAuthenticated) {
        return (
            <header className="fixed top-0 z-[99] bg-background flex items-center justify-center shrink-0 h-[56px] max-sm:w-full w-full">
                <div className="w-full min-w-[320px] px-9 flex items-center justify-between max-xs:px-6">
                    <div className="flex items-center gap-3 max-sm:hidden"></div>
                    <div className="sm:hidden">
                        <Link to="/" className="flex">
                            <button>
                                <Image className="h-8 max-sm:h-6" src={LogoLightImage} darkSrc={LogoImage} alt="logo" />
                            </button>
                        </Link>
                    </div>
                    <DropdownMenu open={headerOptionOpened} onOpenChange={setHeaderOptionOpened}>
                        <DropdownMenuTrigger className="focus-visible:outline-0">
                            <div className="flex items-center gap-[10px]">
                                <div className="w-[26px] h-[26px] max-sm:w-[22px] max-sm:h-[22px] rounded-full bg-[#BBC5CD] dark:bg-[#595F65] flex items-center justify-center text-xs">
                                    {/* {userName.toUpperCase().charAt(0)} */}

                                    <User className="w-[18px] h-[18px]" />
                                </div>
                                <div className="text-base flex items-center gap-1">
                                    <span>{userName}</span>
                                    <ChevronDown
                                        className={cn(
                                            'w-[18px] h-[18px] transition-transform duration-200',
                                            headerOptionOpened && 'rotate-180'
                                        )}
                                    />
                                </div>
                            </div>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-[244px]">
                            <DropdownMenuItem className="flex items-center justify-center gap-[14px] focus:text-inherit">
                                <button onClick={handleThemeChange}>
                                    {theme === 'dark' ? (
                                        <Sun className="w-6 h-6  hover:text-icon-hover" strokeWidth={1.5} />
                                    ) : (
                                        <Moon className="w-6 h-6 hover:text-icon-hover" strokeWidth={1.5} />
                                    )}
                                </button>
                                <button className="relative" onClick={toggleLanguage}>
                                    <Image
                                        className="w-6 h-6 block hover:opacity-0"
                                        src={language === 'en' ? KrIcon : EnIcon}
                                        darkSrc={language === 'en' ? KrDarkIcon : EnDarkIcon}
                                        alt={language === 'en' ? 'Korean' : 'English'}
                                    />
                                    <Image
                                        className="w-6 h-6 absolute top-0 left-0 opacity-0 hover:opacity-100"
                                        src={language === 'en' ? KrHoverIcon : EnHoverIcon}
                                        alt={language === 'en' ? 'Korean' : 'English'}
                                    />
                                </button>
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            </header>
        );
    }

    return (
        <>
            <header
                className={`fixed top-0 z-[99] bg-background flex items-center justify-center shrink-0 h-[56px] max-sm:w-full ${
                    isSidebarOpen ? 'w-[calc(100%-240px)]' : 'w-full'
                }`}
            >
                <div className="w-full min-w-[320px] px-9 flex items-center justify-between max-xs:px-6">
                    <div className="flex items-center gap-3 max-sm:hidden">
                        {!isSidebarOpen && (
                            <button
                                className="transition-all hover:text-[#BABCC0]"
                                onClick={() => setIsSidebarOpen(true)}
                            >
                                <Menu className="h-4 w-4" />
                            </button>
                        )}

                        <div className="flex items-center gap-1">
                            <div className="flex items-center gap-[6px]">
                                <div className="text-base font-medium">{workspace?.name}</div>
                                {workspace?.stereo && getWorkspaceBadge(workspace.stereo)}
                            </div>
                            {project && (
                                <>
                                    <div className="text-[#DFE0E2] mr-2 ml-[6px] dark:text-[#3A3C40]">|</div>
                                    <div className="flex items-center gap-x-2">
                                        <div className="text-base font-medium text-point">{project?.name}</div>
                                        <div className="bg-primary text-white py-1 px-2 rounded-md  text-[10px] font-medium">
                                            {t(
                                                project.stereo === 'prod'
                                                    ? 'project.create.production'
                                                    : 'project.create.development'
                                            )}
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                    <div className="sm:hidden">
                        <Link to="/" className="flex">
                            <button>
                                <Image className="h-8 max-sm:h-6" src={LogoLightImage} darkSrc={LogoImage} alt="logo" />
                            </button>
                        </Link>
                    </div>
                    {!ownerInfo && <SharedSpinner />}
                    {!!ownerInfo && (
                        <DropdownMenu open={headerOptionOpened} onOpenChange={setHeaderOptionOpened}>
                            <DropdownMenuTrigger className="focus-visible:outline-0">
                                <div className="flex items-center gap-[10px]">
                                    <Avatar className="w-[26px] h-[26px] border-0 bg-[#BBC5CD] dark:bg-[#595F65] p-0">
                                        <AvatarImage src={profile?.$user?.photo} alt="@shadcn" />
                                        <AvatarFallback className="bg-[#BBC5CD] dark:bg-[#595F65]">
                                            <User className="w-[18px] h-[18px]" />
                                        </AvatarFallback>
                                    </Avatar>
                                    <div className="text-base flex items-center gap-1">
                                        <span>{userName}</span>
                                        <ChevronDown
                                            className={cn(
                                                'w-[18px] h-[18px] transition-transform duration-200',
                                                headerOptionOpened && 'rotate-180'
                                            )}
                                        />
                                    </div>
                                </div>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-[244px]">
                                <div>
                                    <div className="text-[16px] font-medium">{userName}</div>
                                    {!!ownerInfo.email && (
                                        <div className="text-sm text-placeholder">{ownerInfo.email}</div>
                                    )}
                                </div>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem className="flex flex-col space-y-3 ">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        className="w-full bg-[#f4f5f5] dark:bg-[#454545] border-transparent text-title hover:border-sub-title"
                                        onClick={onTransferClick}
                                    >
                                        Open EurekaBox
                                    </Button>
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuLabel>MY</DropdownMenuLabel>
                                <div className="bg-[#F4F5F5] dark:bg-[#3A3C40] rounded-md flex flex-col gap-1 py-[6px] px-2">
                                    <DropdownMenuItem
                                        className="text-sm font-normal p-0 hover:underline "
                                        onClick={() => navigate('/my-page?tab=info')}
                                    >
                                        {t('header.personalInfo')}
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator className="m-0 !bg-divider" />
                                    <DropdownMenuItem
                                        className="text-sm font-normal p-0 hover:underline "
                                        onClick={() => navigate('/my-page?tab=payment')}
                                    >
                                        {t('header.billing')}
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator className="m-0 !bg-divider" />
                                    <DropdownMenuItem
                                        className="text-sm font-normal p-0 hover:underline "
                                        onClick={() => navigate('/my-page?tab=subscriptions')}
                                    >
                                        {t('header.subscriptions')}
                                    </DropdownMenuItem>
                                </div>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem className="font-medium " onClick={() => setShowContactModal(true)}>
                                    Contact Us
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem className="flex items-center justify-center gap-[14px] mt-3 focus:text-inherit">
                                    <button onClick={handleThemeChange}>
                                        {theme === 'dark' ? (
                                            <Sun className="w-6 h-6  hover:text-icon-hover" strokeWidth={1.5} />
                                        ) : (
                                            <Moon className="w-6 h-6 hover:text-icon-hover" strokeWidth={1.5} />
                                        )}
                                    </button>
                                    <button className="relative" onClick={toggleLanguage}>
                                        <Image
                                            className="w-6 h-6 block hover:opacity-0"
                                            src={language === 'en' ? KrIcon : EnIcon}
                                            darkSrc={language === 'en' ? KrDarkIcon : EnDarkIcon}
                                            alt={language === 'en' ? 'Korean' : 'English'}
                                        />
                                        <Image
                                            className="w-6 h-6 absolute top-0 left-0 opacity-0 hover:opacity-100"
                                            src={language === 'en' ? KrHoverIcon : EnHoverIcon}
                                            alt={language === 'en' ? 'Korean' : 'English'}
                                        />
                                    </button>
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem className="flex flex-col space-y-3 ">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        className="w-full bg-[#F4F5F5] dark:bg-[#3A3C40] border-transparent text-title hover:border-sub-title"
                                        onClick={logoutMutation.mutate}
                                    >
                                        {t('header.logout')}
                                    </Button>
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    )}
                </div>
            </header>
            {showContactModal && <ContactModal isOpen={showContactModal} onClose={() => setShowContactModal(false)} />}
        </>
    );
};
